import { tva } from '@gluestack-ui/utils/nativewind-utils'
import React from 'react'

import { Box } from '../box'

export type HelpLegendDotShape = 'square' | 'dot' | 'line'

export type HelpLegendDotProps = {
    /**
     * Marker colour — a runtime/data-driven value (hex/rgb), e.g.
     * `colors.graphics.teal`. Help legends map domain colours that can't be a
     * static token, so it's applied as an inline colour (the established
     * runtime-colour pattern), not a Tailwind class.
     */
    color: string
    /** Square edge / line length / circle diameter in px. Default 12. */
    size?: number
    /** Marker shape: rounded `square` (default), `dot` (circle) or `line` (2px bar). */
    shape?: HelpLegendDotShape
    className?: string
    testID?: string
}

const dotStyle = tva({
    base: 'shrink-0',
    variants: {
        shape: {
            square: 'rounded-[2px]',
            dot: 'rounded-full',
            line: 'rounded-full',
        },
    },
    defaultVariants: {
        shape: 'square',
    },
})

/**
 * The coloured marker of a help legend entry (universal replacement for the
 * legacy `aos-ui` `LegendItemDot` used inside the dashboard Help modal). A
 * rounded square by default, matching the Figma help legends; `dot` and `line`
 * cover circular and thin-rule legends.
 */
export const HelpLegendDot: React.FC<HelpLegendDotProps> = ({
    color,
    size = 12,
    shape = 'square',
    className,
    testID,
}) => {
    const dimensions = shape === 'line' ? { width: size, height: 2 } : { width: size, height: size }
    return (
        <Box
            className={dotStyle({ shape, class: className })}
            style={{ backgroundColor: color, ...dimensions }}
            testID={testID}
        />
    )
}

HelpLegendDot.displayName = 'HelpLegendDot'
